import React from 'react';
import { CurrencyIcon, CompanyIcon } from '../TooltipIcons';
import { formatCurrency } from '../../../services/marketPerformanceService';
import type { CurrencyBreakdown, CompanyBreakdown } from '../../../services/marketPerformanceService';

/** Props for the custom chart tooltip component */
export interface ChartTooltipProps {
  /** Formatted date string for the data point */
  dateStr: string;
  /** Gross Margin Revenue amount */
  gmr: number;
  /** Yield percentage value */
  yieldValue: number;
  /** Accounts Receivable awarded amount */
  arAwarded: number;
  /** Number of divisions awarded */
  divisionsAwarded: number;
  /** Currency code (e.g., 'USD') */
  currency: string;
  /** Optional breakdown by currency */
  currencyBreakdown?: CurrencyBreakdown[];
  /** Optional breakdown by company */
  companyBreakdown?: CompanyBreakdown[];
}

const tooltipStyles = {
  container: {
    background: '#fff',
    borderRadius: '12px',
    padding: '16px',
    minWidth: '240px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0',
  } as React.CSSProperties,
  date: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
    letterSpacing: '-0.01em',
  } as React.CSSProperties,
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  } as React.CSSProperties,
  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  } as React.CSSProperties,
  statLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1f2937',
  } as React.CSSProperties,
  statValueGreen: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#06A76C',
  } as React.CSSProperties,
  section: {
    borderTop: '1px solid #f0f0f0',
    paddingTop: '12px',
    marginTop: '4px',
  } as React.CSSProperties,
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginBottom: '10px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  } as React.CSSProperties,
  breakdownLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  } as React.CSSProperties,
  breakdownValues: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as React.CSSProperties,
  breakdownValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1f2937',
  } as React.CSSProperties,
  breakdownYield: {
    fontSize: '12px',
    color: '#9ca3af',
  } as React.CSSProperties,
  breakdownOriginal: {
    fontSize: '11px',
    color: '#9ca3af',
  } as React.CSSProperties,
};

/**
 * Custom tooltip component for the Market Performance Chart.
 * Displays GMR, Yield, A/R Awarded, Divisions, and optional breakdowns.
 */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  dateStr,
  gmr,
  yieldValue,
  arAwarded,
  divisionsAwarded,
  currency,
  currencyBreakdown,
  companyBreakdown,
}) => (
  <div style={tooltipStyles.container}>
    <div style={tooltipStyles.header}>
      <span style={tooltipStyles.date}>{dateStr}</span>
    </div>

    <div style={tooltipStyles.statsGrid}>
      <div style={tooltipStyles.statItem}>
        <span style={tooltipStyles.statLabel}>GMR</span>
        <span style={tooltipStyles.statValue}>{formatCurrency(gmr, currency)}</span>
      </div>
      <div style={tooltipStyles.statItem}>
        <span style={tooltipStyles.statLabel}>Yield</span>
        <span style={tooltipStyles.statValueGreen}>{yieldValue.toFixed(2)}%</span>
      </div>
      <div style={tooltipStyles.statItem}>
        <span style={tooltipStyles.statLabel}>A/R Awarded</span>
        <span style={tooltipStyles.statValue}>{formatCurrency(arAwarded, currency)}</span>
      </div>
      <div style={tooltipStyles.statItem}>
        <span style={tooltipStyles.statLabel}>Divisions</span>
        <span style={tooltipStyles.statValue}>{divisionsAwarded}</span>
      </div>
    </div>

    {currencyBreakdown && currencyBreakdown.length > 0 && (
      <div style={tooltipStyles.section}>
        <div style={tooltipStyles.sectionHeader}>
          <CurrencyIcon />
          <span>By Currency</span>
        </div>
        {currencyBreakdown.map((item) => (
          <div key={item.currencyOriginal} style={tooltipStyles.breakdownRow}>
            <span style={tooltipStyles.breakdownLabel}>{item.currencyOriginal}</span>
            <div style={tooltipStyles.breakdownValues}>
              {item.currencyOriginal !== currency && (
                <span style={tooltipStyles.breakdownOriginal}>
                  {formatCurrency(item.clearedEarnOriginal, item.currencyOriginal)}
                </span>
              )}
              <span style={tooltipStyles.breakdownValue}>{formatCurrency(item.clearedEarn, currency)}</span>
              <span style={tooltipStyles.breakdownYield}>{item.clearedYield.toFixed(2)}%</span>
            </div>
          </div>
        ))}
      </div>
    )}

    {companyBreakdown && companyBreakdown.length > 0 && (
      <div style={tooltipStyles.section}>
        <div style={tooltipStyles.sectionHeader}>
          <CompanyIcon />
          <span>By Company</span>
        </div>
        {companyBreakdown.map((item) => (
          <div key={item.makerUuid} style={tooltipStyles.breakdownRow}>
            <span style={tooltipStyles.breakdownLabel}>{item.makerDivisionName}</span>
            <div style={tooltipStyles.breakdownValues}>
              <span style={tooltipStyles.breakdownValue}>{formatCurrency(item.clearedEarn, currency)}</span>
              <span style={tooltipStyles.breakdownYield}>{item.clearedYield.toFixed(2)}%</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);
